/**
 * Turn a proposal row into what the card should say.
 *
 * The wording matters more than it looks. "Approved" describes a click;
 * "Sent" describes a side effect that really happened. Conflating them is how
 * a user comes to believe an email went out when the provider rejected it, so
 * every settled state below names the outcome, not the intent.
 */
const OUTCOMES = {
  executed: { label: 'Sent', tone: 'success' },
  approved: { label: 'Approved', tone: 'success' },
  executing: { label: 'Sending…', tone: 'muted' },
  declined: { label: 'Cancelled', tone: 'muted' },
  expired: { label: 'Expired — not sent', tone: 'muted' },
  failed: { label: "Couldn't send", tone: 'error' },
  contract_blocked: { label: 'Action blocked', tone: 'error' },
  connection_required: { label: 'Connection required', tone: 'error' },
  execution_unknown: { label: 'Send status unknown', tone: 'error' },
}

const FINAL_STATUSES = new Set([
  'approved', 'executed', 'declined', 'expired', 'failed',
  'contract_blocked', 'connection_required', 'execution_unknown',
])

export function isFinalProposalStatus(status) {
  return FINAL_STATUSES.has(String(status ?? ''))
}

export function proposalOutcome(row = {}) {
  const status = String(row?.status ?? '') || null
  if (!status) {
    return { status, settled: false, resolved: false, label: null, tone: null, error: null }
  }
  if (status === 'pending') {
    return { status, settled: false, resolved: true, label: null, tone: null, error: null }
  }
  const outcome = status ? OUTCOMES[status] : null
  if (!outcome) {
    // A proposal can execute only from the exact durable `pending` state.
    // Failing closed here prevents a new server status from turning a card
    // into an accidental retry of a potentially dispatched provider write.
    return {
      status,
      settled: true,
      resolved: true,
      label: 'Action status unavailable',
      tone: 'error',
      error: row?.error || 'The action is not in a state that can be approved again.',
    }
  }
  return {
    status,
    settled: status !== 'pending',
    resolved: true,
    label: outcome.label,
    tone: outcome.tone,
    error: row?.error ?? null,
  }
}

export function mergeProposalOutcome(current, next) {
  if (isFinalProposalStatus(current?.status) && !isFinalProposalStatus(next?.status)) {
    return current
  }
  return next
}
